import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Calendar, Loader2, Copy, ExternalLink, ShieldCheck, User, DollarSign, HeartHandshake, CreditCard, Building2, Info } from "lucide-react";
import { api } from "../../lib/api";

const WEEKDAY_LABEL = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
const money = (v) => (v == null ? "—" : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }));

/**
 * Modal para o consultor reservar uma sessão em nome da família — dentro do
 * caso. Cria booking com `case_id` vinculado, `consultant_id` = consultor
 * logado (garante comissão automática), envia link Stripe pra família por
 * e-mail e devolve o link pro consultor copiar/enviar via WhatsApp.
 *
 * Iter 68.60 — suporte a duas modalidades:
 *   • Particular → Stripe checkout (fluxo atual)
 *   • Convênio  → skip Stripe, cria booking ELIGIBILITY_PENDING (Admin confirma)
 * As operadoras/planos exibidos são apenas as com credencial ATIVA para
 * aquele profissional; a validação canônica acontece no backend (nunca
 * confiar apenas no frontend).
 */
export const BookProfessionalModal = ({ caseData, professional, onClose, onBooked }) => {
  const [prof, setProf] = useState(professional);
  const [scheduledAt, setScheduledAt] = useState("");
  const [notes, setNotes] = useState("");
  const [famEmail, setFamEmail] = useState("");
  const [famName, setFamName] = useState(caseData?.family_name || "");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checkout, setCheckout] = useState(null);
  const [insurancePending, setInsurancePending] = useState(null);

  // Modo: particular | insurance
  const [mode, setMode] = useState("particular");
  const [acceptedInsurers, setAcceptedInsurers] = useState([]);
  const [insurerId, setInsurerId] = useState("");
  const [planId, setPlanId] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [holderName, setHolderName] = useState("");
  const [insLoading, setInsLoading] = useState(false);

  useEffect(() => {
    if (professional?.id) {
      api.get(`/consultant/professionals/${professional.id}`)
        .then(({ data }) => setProf({ ...professional, ...data }))
        .catch(() => {
          api.get(`/professionals/${professional.id}`)
            .then(({ data }) => setProf({ ...professional, ...data }))
            .catch(() => {});
        });
    }
    if (caseData?.family_id) {
      api.get(`/consultant/cases/${caseData.id}/family-contact`)
        .then(({ data }) => {
          setFamEmail(data.email || "");
          if (data.name && !famName) setFamName(data.name);
        })
        .catch(() => {});
    }
  }, [professional?.id, caseData?.id]);  // eslint-disable-line

  // Carrega credenciamentos ATIVOS do profissional só quando o modo Convênio
  // é escolhido. O endpoint público já aplica a regra absoluta (retorna
  // apenas credenciais com status=active E valid_until no futuro).
  useEffect(() => {
    if (mode !== "insurance" || !prof?.id) return;
    setInsLoading(true);
    api.get(`/convenios/professionals/${prof.id}/credentials`)
      .then(({ data }) => setAcceptedInsurers(Array.isArray(data) ? data : []))
      .catch(() => setAcceptedInsurers([]))
      .finally(() => setInsLoading(false));
  }, [mode, prof?.id]);

  // Se trocar operadora, reseta plano (deve pertencer àquela operadora)
  useEffect(() => {
    setPlanId("");
  }, [insurerId]);

  const availabilityByDay = useMemo(() => {
    const g = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
    (prof?.availability || []).forEach((w) => {
      if (g[w.weekday] != null) g[w.weekday].push(`${w.start}-${w.end}`);
    });
    return g;
  }, [prof]);

  // Operadoras únicas (podem haver várias credenciais da mesma operadora
  // com planos diferentes; agrupamos)
  const uniqueInsurers = useMemo(() => {
    const map = new Map();
    (acceptedInsurers || []).forEach((c) => {
      if (!map.has(c.insurer_id)) {
        map.set(c.insurer_id, {
          insurer_id: c.insurer_id,
          insurer_name: c.insurer_name,
          insurer_slug: c.insurer_slug,
          insurer_logo_url: c.insurer_logo_url,
          teleconsultation_allowed: c.teleconsultation_allowed,
        });
      }
    });
    return Array.from(map.values());
  }, [acceptedInsurers]);

  // Planos disponíveis para operadora selecionada (só ativos + ligados ao
  // profissional) — carregados via /convenios/insurers/{id}/plans e filtrados
  // pelos plan_ids que o profissional efetivamente aceita. Se a credencial
  // não tem plan_id (aceita "todos"), oferecemos a lista completa.
  const [allPlansForInsurer, setAllPlansForInsurer] = useState([]);
  useEffect(() => {
    if (!insurerId) { setAllPlansForInsurer([]); return; }
    api.get(`/convenios/insurers/${insurerId}/plans`)
      .then(({ data }) => setAllPlansForInsurer(Array.isArray(data) ? data : []))
      .catch(() => setAllPlansForInsurer([]));
  }, [insurerId]);

  const availablePlans = useMemo(() => {
    if (!insurerId) return [];
    // credenciais desse profissional pra essa operadora
    const proCreds = (acceptedInsurers || []).filter((c) => c.insurer_id === insurerId);
    if (proCreds.length === 0) return [];
    // Se qualquer credencial NÃO tem plan_id específico → aceita todos
    const acceptsAny = proCreds.some((c) => !c.plan_id);
    if (acceptsAny) return allPlansForInsurer;
    const allowedIds = new Set(proCreds.map((c) => c.plan_id).filter(Boolean));
    return allPlansForInsurer.filter((p) => allowedIds.has(p.id));
  }, [insurerId, acceptedInsurers, allPlansForInsurer]);

  const submit = async () => {
    if (!scheduledAt) { toast.error("Selecione a data e o horário da consulta"); return; }
    if (!famEmail || !famEmail.includes("@")) { toast.error("Informe um e-mail válido pra família receber o link"); return; }
    if (!termsAccepted) { toast.error("Confirme que apresentou as regras da consulta à família"); return; }
    if (mode === "insurance") {
      if (!insurerId) { toast.error("Selecione a operadora do convênio"); return; }
      if (!cardNumber || cardNumber.replace(/\s/g, "").length < 4) {
        toast.error("Informe o número da carteirinha do convênio");
        return;
      }
      if (!holderName.trim()) { toast.error("Informe o nome do titular da carteirinha"); return; }
      if (uniqueInsurers.length && !uniqueInsurers.some((u) => u.insurer_id === insurerId)) {
        toast.error("Operadora não autorizada para este profissional");
        return;
      }
    }
    setBusy(true);
    try {
      const iso = new Date(scheduledAt).toISOString();
      const payload = {
        professional_id: prof.id,
        family_email: famEmail.trim().toLowerCase(),
        family_name: famName.trim(),
        scheduled_at: iso,
        origin_url: window.location.origin,
        notes: notes.trim(),
        case_id: caseData?.id,
        family_terms_accepted: true,
        payment_source: mode,
      };
      if (mode === "insurance") {
        payload.insurance_meta = {
          insurer_id: insurerId,
          plan_id: planId || null,
          card_number: cardNumber.replace(/\s/g, ""),
          holder_name: holderName.trim(),
        };
      }
      const { data } = await api.post("/consultant/bookings/create", payload);
      if (data?.insurance_pending) {
        setInsurancePending(data);
        toast.success("Reserva criada — aguardando elegibilidade do convênio.");
      } else {
        setCheckout({ url: data.checkout_url, value: data.value });
        toast.success("Reserva criada! Link enviado à família.");
      }
      onBooked && onBooked(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao criar reserva");
    } finally { setBusy(false); }
  };

  const copyLink = () => {
    if (!checkout?.url) return;
    navigator.clipboard.writeText(checkout.url);
    toast.success("Link copiado — cole no WhatsApp da família");
  };

  const openWhatsapp = () => {
    if (!checkout?.url) return;
    const msg = encodeURIComponent(
      `Oi ${famName.split(" ")[0] || ""}! Sua consulta foi agendada. ` +
      `Confirme o pagamento por este link seguro (Stripe): ${checkout.url}`
    );
    window.open(`https://wa.me/?text=${msg}`, "_blank", "noopener");
  };

  const done = checkout || insurancePending;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose} data-testid="book-prof-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-4"
        onClick={(e) => e.stopPropagation()}>

        <div className="p-5 border-b border-slate-200 flex items-start justify-between gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-violet-700 font-bold">Reservar sessão em nome da família</div>
            <h2 className="text-xl font-bold text-[#0B2B62] mt-0.5 truncate">
              {prof.display_name || prof.name}
            </h2>
            <div className="text-xs text-slate-500 mt-0.5">
              {[prof.council_type, prof.council_number, prof.council_state].filter(Boolean).join(" ")}
              {prof.session_value != null && <> · <b className="text-slate-700">{money(prof.session_value)}</b>/sessão</>}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-500 hover:text-slate-800">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!done ? (
          <div className="p-5 space-y-4">
            {/* Mode toggle */}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-1 flex gap-1" data-testid="book-mode-toggle">
              <button
                type="button"
                onClick={() => setMode("particular")}
                data-testid="book-mode-particular"
                className={`flex-1 py-2 rounded-md text-xs font-bold transition ${mode === "particular"
                  ? "bg-white text-violet-700 shadow-sm ring-1 ring-slate-200"
                  : "text-slate-500 hover:text-slate-700"}`}
              >
                <DollarSign className="w-3.5 h-3.5 inline mr-1" /> Particular (Stripe)
              </button>
              <button
                type="button"
                onClick={() => setMode("insurance")}
                data-testid="book-mode-insurance"
                className={`flex-1 py-2 rounded-md text-xs font-bold transition ${mode === "insurance"
                  ? "bg-white text-emerald-700 shadow-sm ring-1 ring-emerald-200"
                  : "text-slate-500 hover:text-slate-700"}`}
              >
                <HeartHandshake className="w-3.5 h-3.5 inline mr-1" /> Convênio
              </button>
            </div>

            {/* Family */}
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">
                  <User className="w-3 h-3" /> Nome da família
                </span>
                <input value={famName} onChange={(e) => setFamName(e.target.value)}
                  data-testid="book-fam-name"
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                  E-mail (recebe {mode === "insurance" ? "confirmação da análise" : "link Stripe"})
                </span>
                <input type="email" value={famEmail} onChange={(e) => setFamEmail(e.target.value)}
                  data-testid="book-fam-email"
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
              </label>
            </div>

            {/* Insurance form */}
            {mode === "insurance" && (
              <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/40 p-4 space-y-3" data-testid="book-insurance-form">
                <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-bold flex items-center gap-1">
                  <HeartHandshake className="w-3 h-3" /> Dados do convênio
                </div>
                {insLoading ? (
                  <div className="text-xs text-slate-500 flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> Carregando operadoras aceitas por este profissional…
                  </div>
                ) : uniqueInsurers.length === 0 ? (
                  <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded p-2" data-testid="book-no-insurance">
                    <Info className="w-3 h-3 inline mr-1" />
                    Este profissional <b>não tem credenciamento ativo</b> em nenhuma operadora habilitada para teleconsulta.
                    Escolha <b>Particular</b> ou selecione outro profissional.
                  </div>
                ) : (
                  <>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">
                        <Building2 className="w-3 h-3" /> Operadora
                      </span>
                      <select value={insurerId} onChange={(e) => setInsurerId(e.target.value)}
                        data-testid="book-ins-insurer"
                        className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
                        <option value="">— Selecione —</option>
                        {uniqueInsurers.map((u) => (
                          <option key={u.insurer_id} value={u.insurer_id}>{u.insurer_name}</option>
                        ))}
                      </select>
                    </label>
                    {insurerId && (
                      <label className="block">
                        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                          Plano (opcional se o profissional aceita qualquer plano)
                        </span>
                        <select value={planId} onChange={(e) => setPlanId(e.target.value)}
                          data-testid="book-ins-plan"
                          className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
                          <option value="">— Qualquer plano —</option>
                          {availablePlans.map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      </label>
                    )}
                    <div className="grid sm:grid-cols-2 gap-3">
                      <label className="block">
                        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">
                          <CreditCard className="w-3 h-3" /> Nº carteirinha
                        </span>
                        <input value={cardNumber}
                          onChange={(e) => setCardNumber(e.target.value.replace(/[^0-9A-Za-z ]/g, "").slice(0, 30))}
                          placeholder="0000 0000 0000 0000"
                          data-testid="book-ins-card"
                          autoComplete="off"
                          className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono tracking-wider" />
                        <span className="text-[10px] text-slate-500 mt-0.5 block">
                          Dado sensível — armazenado com acesso restrito, mascarado nas telas.
                        </span>
                      </label>
                      <label className="block">
                        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                          Nome do titular
                        </span>
                        <input value={holderName} onChange={(e) => setHolderName(e.target.value)}
                          data-testid="book-ins-holder"
                          className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
                      </label>
                    </div>
                    <div className="text-[11px] text-emerald-900 bg-emerald-100/60 border border-emerald-200 rounded p-2 leading-relaxed" data-testid="book-ins-notice">
                      <b>Fluxo Convênio:</b> a reserva é criada com status <b>ELIGIBILITY_PENDING</b>.
                      A RAF confirma a cobertura com a operadora e libera a consulta em até 2 dias úteis.
                      Sem cobrança imediata via Stripe.
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Slot */}
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Data e horário
              </span>
              <input type="datetime-local" value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                data-testid="book-datetime"
                className="mt-1 w-full sm:w-72 px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </label>

            {/* Availability hint */}
            {prof.availability && prof.availability.length > 0 ? (
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-700" data-testid="book-availability-panel">
                <div className="font-semibold text-slate-800 mb-1 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Agenda declarada pelo profissional
                </div>
                <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-0.5">
                  {[0, 1, 2, 3, 4, 5, 6].map((d) => (
                    availabilityByDay[d].length > 0 && (
                      <li key={d}><b>{WEEKDAY_LABEL[d]}:</b> {availabilityByDay[d].join(", ")}</li>
                    )
                  ))}
                </ul>
                <p className="mt-2 text-[10px] text-slate-500 leading-relaxed">
                  Se a família precisa de um horário fora dessa grade, converse com o profissional
                  pelo WhatsApp para negociar uma flexibilização antes de reservar.
                </p>
              </div>
            ) : (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid="book-no-availability">
                <div className="font-semibold mb-1 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Este profissional ainda não declarou a agenda
                </div>
                Combine o dia/horário direto com o profissional via WhatsApp e digite abaixo. A
                reserva só passa se o horário estiver dentro da agenda dele — se estiver fora, peça
                a ele para cadastrar o horário no painel antes de confirmar.
              </div>
            )}

            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-900" data-testid="book-online-notice">
              🎥 <b>100% online</b> — todas as consultas do RAF Direct acontecem pela plataforma via videoconferência segura (JaaS 8x8). Não há atendimento presencial.
            </div>

            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Observações internas</span>
              <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Ex.: paciente pediu horário fora do expediente"
                data-testid="book-notes"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm resize-none" />
            </label>

            <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50/50 p-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  data-testid="book-terms-accepted"
                  className="mt-0.5 h-4 w-4 accent-emerald-700 shrink-0" />
                <span className="text-xs text-emerald-900 leading-relaxed">
                  Declaro que <b>apresentei as regras da consulta à família</b> (no-show 50/50,
                  cancelamento com 24h de antecedência, feriados nacionais bloqueados) e obtive
                  consentimento antes de gerar {mode === "insurance" ? "a reserva por convênio" : "o link de pagamento"}.
                </span>
              </label>
            </div>

            {/* Comissão preview — só faz sentido pra particular (Stripe) */}
            {mode === "particular" && prof.session_value != null && (
              <div className="rounded-lg border border-violet-200 bg-violet-50/60 p-3 text-xs text-violet-900" data-testid="book-commission-preview">
                <div className="font-bold uppercase tracking-widest text-[10px] mb-1 flex items-center gap-1">
                  <DollarSign className="w-3 h-3" /> Sua comissão automática
                </div>
                <div>
                  Valor da sessão: <b>{money(prof.session_value)}</b> · RAF (35%): {money(prof.session_value * 0.35)} ·
                  <b className="text-violet-700"> Você recebe (10% da fatia RAF): {money(prof.session_value * 0.035)}</b>
                </div>
              </div>
            )}
          </div>
        ) : insurancePending ? (
          <div className="p-5 space-y-4" data-testid="book-insurance-success">
            <div className="rounded-lg border-2 border-emerald-400 bg-emerald-50 p-4">
              <div className="flex items-center gap-2 font-bold text-emerald-900">
                <ShieldCheck className="w-5 h-5" /> Reserva por Convênio criada
              </div>
              <p className="text-sm text-emerald-900 mt-1">
                Status: <b>{insurancePending.status || "eligibility_pending"}</b> — a RAF vai
                confirmar a cobertura com a operadora <b>{insurancePending.insurer_name}</b>
                {insurancePending.plan_name && <> (plano <b>{insurancePending.plan_name}</b>)</>}.
              </p>
              <ul className="text-xs text-emerald-900 mt-3 space-y-0.5">
                <li>• Nada foi cobrado agora.</li>
                <li>• Carteirinha: <b className="font-mono">{insurancePending.card_masked || "—"}</b></li>
                <li>• A família recebeu um e-mail informando a análise em curso.</li>
                <li>• Após liberação, a consulta aparece com a sala virtual JaaS ativa.</li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-4" data-testid="book-success">
            <div className="rounded-lg border-2 border-emerald-400 bg-emerald-50 p-4">
              <div className="flex items-center gap-2 font-bold text-emerald-900">
                <ShieldCheck className="w-5 h-5" /> Reserva criada e link enviado
              </div>
              <p className="text-sm text-emerald-900 mt-1">
                A família recebeu o link Stripe por e-mail. Se quiser reforçar, copie ou
                envie via WhatsApp abaixo.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={copyLink} data-testid="book-copy-link"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-white border border-slate-300 text-slate-700 text-sm hover:bg-slate-50">
                  <Copy className="w-3.5 h-3.5" /> Copiar link
                </button>
                <button onClick={openWhatsapp} data-testid="book-share-whatsapp"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#25D366] hover:bg-[#1DA851] text-white text-sm font-semibold">
                  <ExternalLink className="w-3.5 h-3.5" /> Compartilhar por WhatsApp
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} data-testid="book-close"
            className="px-4 py-2 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm">
            {done ? "Fechar" : "Cancelar"}
          </button>
          {!done && (
            <button onClick={submit} disabled={busy}
              data-testid="book-submit"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-white text-sm font-bold disabled:opacity-50 ${mode === "insurance"
                ? "bg-emerald-600 hover:bg-emerald-700"
                : "bg-violet-600 hover:bg-violet-700"}`}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> :
                (mode === "insurance" ? <HeartHandshake className="w-4 h-4" /> : <Calendar className="w-4 h-4" />)}
              {mode === "insurance" ? "Criar reserva por convênio" : "Criar reserva e enviar link"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
